import React, { useState, useEffect } from 'react';
import { LandingPageConfig, TeamMember } from '../types';
import { configAPI } from '../services/api';
import logo from '../assets/logo.png'; // always use local asset per request
import { Link } from 'react-router-dom';

const LandingPage: React.FC = () => {
  const [config, setConfig] = useState<LandingPageConfig | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [configResponse] = await Promise.all([
          configAPI.getLandingConfig(),
        ]);

        const cfg = (configResponse as any)?.data?.data ?? (configResponse as any)?.data ?? configResponse;
        setConfig(cfg);
        // team members were saved in landing_page_config key "team_members" as JSON
        let tm: any[] = [];
        try {
          const tmValue = cfg?.team_members ?? cfg?.value; // defensive
          if (typeof tmValue === "string") {
            tm = JSON.parse(tmValue);
          } else if (Array.isArray(tmValue)) {
            tm = tmValue;
          } else if (cfg.team_members && typeof cfg.team_members === "string") {
            tm = JSON.parse(cfg.team_members);
          } else {
            // attempt to find config record by key
            // (configAPI should return flat object; adjust if your API shape differs)
            const teamConfig = cfg && (cfg as any)["team_members"];
            if (teamConfig) tm = typeof teamConfig === "string" ? JSON.parse(teamConfig) : teamConfig;
          }
        } catch (err) {
          console.warn("Failed to parse team members from config:", err);
          tm = [];
        }
        setTeamMembers(tm);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Error Loading Page</h1>
          <p className="text-gray-600">Unable to load the landing page content.</p>
        </div>
      </div>
    );
  }

  // Always use the local bundled logo (per requirement)
  const logoSrc = logo;

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className=" mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            {/* Logo */}
            <div className="flex items-center">
              <div className="flex items-center space-x-3">
                {config?.company_logo ? (
                  <img
                    src={logoSrc}
                    alt="Company Logo"
                    className="w-24 h-24 object-contain rounded-md flex-shrink-0"
                    style={{ display: "block" }}
                    onError={(e) => console.error('Logo failed to load:', logoSrc, e)}
                  />
                ) : (
                  <div className="w-12 h-12 bg-primary-500 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 2L3 7v11h4v-6h6v6h4V7l-7-5z" />
                    </svg>
                  </div>
                )}
                <div>
                  <h1 className="text-2xl font-bold text-primary-500">{config.company_name}</h1>
                  <p className="text-sm text-gray-600">{config.company_subtitle}</p>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex space-x-4">
              <Link to="/login" className="btn-primary">
                Login
              </Link>
              <Link to="/signup" className="btn-secondary">
                SignUp
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className=" mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Team Section */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Team</h2>
            <p className="text-lg text-gray-600">Meet the people behind SR Patel Jewels</p>
          </div>

          {/* Team Timeline */}
          <div className="relative pb-12 overflow-visible">
            {/* Horizontal line across the section (behind content) */}
            {/* Keep the line exactly aligned with the dot center */}
            <div className="pointer-events-none absolute inset-x-0 top-12 h-[3px] bg-primary-700 z-0"></div>

            {/* Members equally spaced */}
            <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 z-10">
              {teamMembers.map((member, index) => (
                <div key={member.name ?? index} className="flex flex-col items-center text-center">
                  {/* Dot on the line with soft shadow and vertical stem */}
                  <div className="relative h-28">
                    {/* intersection shadow ringed dot */}
                    <div className="absolute left-1/2 -translate-x-1/2 top-12 -translate-y-1/2 w-6 h-6 rounded-full bg-primary-800 ring-4 ring-secondary-50 shadow-[0_3px_10px_rgba(0,0,0,0.35)]"></div>
                    {/* vertical stem exactly 90° to the horizontal line */}
                    <div className="absolute left-1/2 -translate-x-1/2 top-12 h-16 w-[2px] bg-primary-800"></div>
                  </div>

                  {/* Member Info */}
                  <div className="max-w-xs mt-1">
                    <h3 className="text-xl font-serif font-bold text-gray-900 leading-tight mb-1 whitespace-pre-line">
                      {member.name}
                    </h3>
                    <p className="text-primary-700 font-semibold mb-1">{member.phone}</p>
                    {member.email && (<p className="text-gray-600 text-sm">{member.email}</p>)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t">
        <div className=" mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 md:gap-x-40 lg:gap-x-56 md:gap-y-12">
            {/* Contact Details */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Details</h3>
              <div className="space-y-2 text-gray-600">
                <p className="font-medium">{config.company_address}</p>
                <p>
                  <span className="font-medium">GST No.:</span> {config.gst_number}
                </p>
              </div>
            </div>

            {/* Contact Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Get in Touch</h3>
              <div className="space-y-3">
                {/* Phone Numbers */}
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-primary-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                  <div>
                    {config.phone_numbers.map((phone, index) => (
                      <p key={index} className="text-gray-600">
                        {phone}
                      </p>
                    ))}
                  </div>
                </div>

                {/* WhatsApp */}
                <div className="flex items-center space-x-2">
                  <svg
                    className="w-5 h-5"
                    viewBox="0 0 512 512"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fill="#E0E0E0"
                      d="M256 0C114.62 0 0 114.62 0 256c0 47.24 12.67 92.07 36.7 131.8L24 512l126.4-33.2A255.6 255.6 0 00256 512c141.38 0 256-114.62 256-256S397.38 0 256 0z"
                    />
                    <path
                      fill="#4CAF50"
                      d="M256 48c114.87 0 208 93.13 208 208 0 114.87-93.13 208-208 208-36.46 0-72.03-9.5-103.74-27.6l-6.87-4-75.06 19.7 20-73.5-4.47-7.34A207.3 207.3 0 0148 256c0-114.87 93.13-208 208-208z"
                    />
                    <path
                      fill="#FAFAFA"
                      d="M189.2 147.5c-4.8-10.7-9.9-10.9-14.5-11.1-3.8-.2-8.1-.2-12.4-.2s-11.4 1.6-17.4 8.1c-6 6.5-22.8 22.3-22.8 54.4s23.3 63.1 26.5 67.4c3.2 4.3 45 72 111 98.1 54.9 21.7 66.1 17.4 78.1 16.3 12-1.1 38.4-15.7 43.8-30.9 5.4-15.2 5.4-28.3 3.8-30.9-1.6-2.7-6-4.3-12.6-7.6s-38.4-19-44.4-21.2c-6-2.2-10.4-3.3-14.9 3.3-4.3 6.5-17.1 21.2-21 25.5-3.8 4.3-7.6 4.8-14.1 1.6-6.5-3.3-27.4-10.1-52.2-32.3-19.3-17.2-32.3-38.3-36.1-44.8-3.8-6.5-.4-10 2.8-13.2 2.9-2.9 6.5-7.6 9.7-11.4 3.2-3.8 4.3-6.5 6.5-10.9 2.2-4.3 1.1-8.1-.5-11.4-1.6-3.3-14.1-35.1-19.9-47.7z"
                    />
                  </svg>

                  <p className="text-gray-600">{config.whatsapp_number}</p>
                </div>

                {/* Email */}
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-primary-500" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                  <p className="text-gray-600">{config.email}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
